// src/components/IDE/ide/adapters/javascript/shim.ts
import { createIDECodeInterpreter, CodeInterpreter } from '../../../CodeRuntime/services/ideCodeInterpreter';
import { createDefaultExecutionContext } from '../../../CodeRuntime/context/executionContext';
import type { Controls, EngineBridge } from '../../core/types';

type LogForwarder = {
    log: (msg: string) => void;
    info: (msg: string) => void;
    warn: (msg: string) => void;
    error: (msg: string) => void;
    complete: () => void;
};

const makeLogForwarder = (bridge?: EngineBridge): LogForwarder => {
    const safe = <T extends any[]>(fn?: (...a: T) => void, ...args: T) => {
        try { fn?.(...args); } catch { /* noop */ }
    };
    const wb = (window as any).__IDE_BRIDGE__;

    return {
        log: (msg) => {
            if ((bridge as any)?.onLog) safe((bridge as any).onLog, msg);
            else safe(wb?.onLog, msg);
        },
        info: (msg) => {
            if ((bridge as any)?.onInfo) safe((bridge as any).onInfo, msg);
            else safe(wb?.onInfo ?? wb?.onLog, msg);
        },
        warn: (msg) => {
            if ((bridge as any)?.onWarn) safe((bridge as any).onWarn, msg);
            else safe(wb?.onWarn ?? wb?.onLog, msg);
        },
        error: (msg) => {
            if ((bridge as any)?.onError) safe((bridge as any).onError, msg);
            else safe(wb?.onError ?? wb?.onLog, msg);
        },
        complete: () => {
            if ((bridge as any)?.onComplete) safe((bridge as any).onComplete);
            else safe(wb?.onComplete);
        },
    };
};

type ListenerDisposer = () => void;

export interface JSGlue {
    compile: (code: string, bridge: EngineBridge, controls: Controls) => Promise<any>;
    runStep: (exec: any, controls: Controls) => Promise<void>;
    runAll: (exec: any, controls: Controls) => Promise<void>;
    stop?: (exec: any) => Promise<void>;
}

const hasBridgeEvents = (bridge: EngineBridge) =>
    !!bridge && typeof (bridge as any).events?.on === 'function' && typeof (bridge as any).events?.off === 'function';

/** Crée des hooks d'événements bindiés. Fallback: écoute des CustomEvent "tilmi:<name>" */
function makeEventHooks(
    bridge: EngineBridge | undefined,
    disposers: Array<() => void>
): { on: (n: string, h: (...a: any[]) => void) => void; off: (n: string, h: (...a: any[]) => void) => void } {
    const events = (bridge as any)?.events;
    if (events && typeof events.on === 'function' && typeof events.off === 'function') {
        // ⚠️ bind pour conserver le this interne
        return { on: events.on.bind(events), off: events.off.bind(events) };
    }
    // Fallback DOM → écoute les CustomEvent "tilmi:<name>" émis côté EventManager.emit(...)
    return {
        on(name, handler) {
            const domName = name.startsWith('tilmi:') ? name : `tilmi:${name}`;
            const domHandler = (e: Event) => {
                const detail = (e as CustomEvent).detail ?? [];
                try {
                    // EventManager.emit envoie un tableau d'args
                    if (Array.isArray(detail)) handler(...detail);
                    else handler(detail);
                } catch (err) {
                    console.error('[JS-IDE] Event handler error for', domName, err);
                }
            };
            window.addEventListener(domName, domHandler as EventListener);
            disposers.push(() => window.removeEventListener(domName, domHandler as EventListener));
        },
        // Quand on est en fallback DOM, on s'appuie sur disposers (pas de off direct par handler)
        off(_name, _handler) { /* noop */ },
    };
}

export const JSRuntimeGlue: JSGlue = {
    async compile(code: string, bridge: EngineBridge, controls: Controls) {
        const logs: string[] = [];
        const fwd = makeLogForwarder(bridge);

        const executionContext = createDefaultExecutionContext((msg: string) => {
            logs.push(msg);
            fwd.log(msg);
        });

        // Permettre au runtime d'accéder aux logs après exécution
        (executionContext as any).__logs = logs;



        const listenerDisposers: ListenerDisposer[] = [];
        let interpreter: CodeInterpreter | null = null;

        // Hooks d'événements (bindés ou fallback DOM)
        const { on, off } = makeEventHooks(bridge, listenerDisposers);

        const handleRegisterEvent = (eventName: string, userFnAst: any, eventParams: string[] = []) => {
            if (!interpreter) return;

            const resolveParam = (raw?: string) => {
                const t = (raw ?? '').trim();
                if (!t || t === '*') return undefined;            // wildcard explicite

                // Si ce n'est PAS un identifiant JS valide → littéral (GUID, "42", etc.)
                const isIdent = /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(t);
                if (!isIdent) return t;

                // Sinon tenter une variable ; si introuvable, garder le littéral fourni
                const v = interpreter!.getVar(t);
                return v === undefined ? t : String(v);
            };

            // keyPress (navigateur)
            if (eventName === 'keyPress') {
                const keys = eventParams.map(String);
                const fn = (e: KeyboardEvent) => {
                    if (keys.length === 0 || keys.includes(e.key)) {
                        interpreter!.injectUserFunctionCall(userFnAst, { key: e.key });
                        interpreter!.runAll();
                    }
                };
                // Use capture phase to bypass editors stopping propagation
                window.addEventListener('keydown', fn, true);
                listenerDisposers.push(() => window.removeEventListener('keydown', fn, true as any));
                return;
            }

            // timers (agnostique moteur)
            if (eventName === 'timerOnce' || eventName === 'timerDuring') {
                const [delayStr] = eventParams;
                let seconds = Number(delayStr);
                if (isNaN(seconds)) seconds = Number(interpreter.getVar(delayStr));
                if (!Number.isFinite(seconds) || seconds <= 0) {
                    executionContext.addLog(`timer : délai invalide (${delayStr}).`);
                    return;
                }
                const cb = () => { interpreter!.injectUserFunctionCall(userFnAst, { seconds }); interpreter!.runAll(); };
                const id = (eventName === 'timerOnce') ? setTimeout(cb, seconds * 1000) : setInterval(cb, seconds * 1000);
                listenerDisposers.push(() => {
                    if (eventName === 'timerOnce') clearTimeout(id as any);
                    else clearInterval(id as any);
                });
                return;
            }

            // ----- Écoutes moteur (bridge.events ou fallback DOM "tilmi:*") -----

            if (eventName === 'collision') {
                const [watchA, watchB] = eventParams;
                const realA = resolveParam(watchA);
                const realB = resolveParam(watchB);

                const fn = (idA: string, idB: string) => {
                    const okA = !realA || idA === realA || idB === realA;
                    const okB = !realB || idA === realB || idB === realB;
                    if (okA && okB) {
                        interpreter!.injectUserFunctionCall(userFnAst, { entityA: idA, entityB: idB });
                        interpreter!.runAll();
                    }
                };
                on('collisionEvent', fn);
                listenerDisposers.push(() => off('collisionEvent', fn));
                return;
            }

            if (eventName === 'textChange') {
                const [watchIdRaw = '*'] = eventParams;
                const watchId = resolveParam(watchIdRaw); // undefined => wildcard

                const fn = (ev: { textId: string; newText: string; oldText: string }) => {
                    if (!watchId || ev.textId === watchId) {
                        interpreter!.injectUserFunctionCall(userFnAst, ev);
                        interpreter!.runAll();
                    }
                };
                on('textChangeEvent', fn);
                listenerDisposers.push(() => off('textChangeEvent', fn));
                return;
            }

            if (eventName === 'pickup') {
                const [watchChar, watchObj] = eventParams;
                const realChar = resolveParam(watchChar);
                const realObj = resolveParam(watchObj);

                const fn = (charId: string, objId: string) => {
                    const okChar = !realChar || charId === realChar;
                    const okObj = !realObj || objId === realObj;
                    if (okChar && okObj) {
                        interpreter!.injectUserFunctionCall(userFnAst, { charId, objId });
                        interpreter!.runAll();
                    }
                };
                on('pickupEvent', fn);
                listenerDisposers.push(() => off('pickupEvent', fn));
                return;
            }

            executionContext.addLog(`(onRegisterEvent) Événement inconnu: ${eventName} (ignoré).`);
        };

        // Crée l’interpréteur et initialise
        interpreter = createIDECodeInterpreter(executionContext, handleRegisterEvent);
        interpreter.init(code);

        // Breakpoints → fournis par l’IDE
        (executionContext as any).__getBreakpoints = () => controls.breakpoints.getAll().map(b => b.line);
        (executionContext as any).__ignoreBreakpoints = () => controls.ignoreBreakpoints();

        // Optionnel: exposer globals côté runtime
        const ideGlobals = (window as any).__IDE_GLOBALS__ ?? {};
        (executionContext as any).__globals = {
            engine: bridge,   // (utile si tu veux l’utiliser)
            ...ideGlobals,    // ← walk_right, say_by_id, ...
        };

        // Hook stop (optionnel)
        (executionContext as any).__onStop = () => {
            try { (bridge as any)?.events?.emit?.('ide:stop'); } catch { }
        };

        const payload = {
            interpreter,
            executionContext,
            logs,
            listenerDisposers,
            bridge,
            dispose() {
                try { this.listenerDisposers.forEach((f: ListenerDisposer) => f()); } catch { }
                this.listenerDisposers = [];
            },
        };
        return payload;
    },

    async runStep(exec, _controls) {
        exec.executionContext.isRunning = true;
        exec.executionContext.isPaused = false;
        await exec.interpreter.step();
    },

    async runAll(exec, _controls) {
        exec.executionContext.isRunning = true;
        exec.executionContext.isPaused = false;
        await exec.interpreter.runAll();

        const fwd = makeLogForwarder(exec.bridge);

        const logs = (exec.executionContext as any).__logs || [];
        if (logs.length > 0) {
            const output = logs.join('\n');
            fwd.log(output);
        }

        fwd.complete();
    },

    async stop(exec) {
        try {
            if (exec.executionContext) {
                exec.executionContext.isRunning = false;
                exec.executionContext.isPaused = false;
            }
            exec.dispose?.();            // nettoie keydown/timers/bridge.events
            exec.interpreter?.stop?.();  // coupe la boucle de l'interpréteur
        } catch { }
    }
};
